/**
 * Canonical Wonde connection status — mirrors the backend `WondeConnectionStatus`
 * enum (Welbee.Data.Models.Enums). Status is the single source of truth for a
 * school's Wonde connection and is keyed on the SchoolId (never on URN), so
 * no-URN / NI schools never collide.
 *
 * Backend serialises the enum as an integer, so these numeric values must stay
 * in lock-step with the BE enum.
 */
export const WondeConnectionStatus = {
  NotConnected: 0,
  Unmatched: 1,
  PendingApproval: 2,
  Approved: 3,
  Declined: 4,
  Revoked: 5,
};

const LABELS = {
  [WondeConnectionStatus.NotConnected]: 'Not connected',
  [WondeConnectionStatus.Unmatched]: 'Not found in Wonde',
  [WondeConnectionStatus.PendingApproval]: 'Pending approval',
  [WondeConnectionStatus.Approved]: 'Approved & syncing',
  [WondeConnectionStatus.Declined]: 'Declined',
  [WondeConnectionStatus.Revoked]: 'Revoked',
};

// MUI Chip colour per status.
const COLORS = {
  [WondeConnectionStatus.NotConnected]: 'default',
  [WondeConnectionStatus.Unmatched]: 'warning',
  [WondeConnectionStatus.PendingApproval]: 'warning',
  [WondeConnectionStatus.Approved]: 'success',
  [WondeConnectionStatus.Declined]: 'error',
  [WondeConnectionStatus.Revoked]: 'error',
};

/** Human-readable label. Prefer the BE-provided StatusLabel; this is the fallback. */
export const getStatusLabel = (status) =>
  LABELS[status] !== undefined ? LABELS[status] : 'Unknown';

/** MUI Chip colour for a status. */
export const getStatusColor = (status) =>
  COLORS[status] !== undefined ? COLORS[status] : 'default';

/** Approved & actively syncing. */
export const isApproved = (status) => status === WondeConnectionStatus.Approved;

/** A request has been sent and is awaiting Wonde's decision. */
export const isPending = (status) =>
  status === WondeConnectionStatus.PendingApproval;

/** Sync settings / runs are only meaningful once a school is approved. */
export const canManageSync = (status) =>
  status === WondeConnectionStatus.Approved;

/**
 * A (re)connection can be requested from any state that is not already
 * pending or approved — i.e. fresh, unmatched, declined or revoked schools.
 */
export const canRequestConnection = (status) =>
  status === WondeConnectionStatus.NotConnected ||
  status === WondeConnectionStatus.Unmatched ||
  status === WondeConnectionStatus.Declined ||
  status === WondeConnectionStatus.Revoked;

/** Declined / revoked are terminal states an admin may want to act on. */
export const isActionableError = (status) =>
  status === WondeConnectionStatus.Declined ||
  status === WondeConnectionStatus.Revoked;

/**
 * Whether there is a connection to clear/withdraw. Anything other than a fresh,
 * never-connected school can be cleared — pending (cancel the request), approved
 * (disconnect & revoke), or a declined/revoked/unmatched leftover.
 */
export const canClearConnection = (status) =>
  status !== undefined &&
  status !== null &&
  status !== WondeConnectionStatus.NotConnected;

/** Sync scope passed to the run endpoint — mirrors BE `WondeSyncScope`. */
export const WondeSyncScope = {
  Staff: 'Staff',
  Students: 'Students',
  All: 'All',
};
